import { getVariantDisplayQuantity } from '../../../../../utils/variant-display';
import { CommonModule } from '@angular/common';
import {
  Component,
  DoCheck,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CategoryDto } from '../../../../../services/categories/categories.service';
import {
  Product,
  ProductsService,
} from '../../../../../services/products/products.service';
import {
  AppTheme,
  ThemeConfig,
  ThemeService,
} from '../../../../../services/theme/theme.service';
import {
  BottomDynamicTab,
  DynamicTabsService,
} from '../../../../../services/dynamic-tabs/dynamic-tabs.service';
import {
  BrandGridConfig,
  CMSSection,
  CollectionBannerConfig,
  HeroShowcaseConfig,
  OfferStripConfig,
  SectionType,
  Tab,
} from '../../types';

@Component({
  selector: 'app-mock-preview',
  standalone: true,
  imports: [CommonModule],
  providers: [],
  templateUrl: './mock-preview.component.html',
  styleUrls: ['./mock-preview.component.scss'],
})
export class MockPreviewComponent implements OnInit, OnDestroy, DoCheck {
  private themeService = inject(ThemeService);
  private productsService = inject(ProductsService);
  private dynamicTabsService = inject(DynamicTabsService);
  private sanitizer = inject(DomSanitizer);
  private timeTimerId: ReturnType<typeof setInterval> | undefined;
  dynamicThemes: AppTheme[] = [];
  currentTime = '9:41';
  batteryLevel = 100;
  private shelfQuerySignatures = new Map<string, string>();
  private shelfSubscriptions = new Map<string, Subscription>();
  private shelfPreviewProducts = new Map<string, any[]>();
  private loadingShelfIds = new Set<string>();
  private failedShelfIds = new Set<string>();
  private categoryPreviewSubscription?: Subscription;
  private dynamicTabSubscription?: Subscription;

  selectedPreviewCategory: any | null = null;
  dynamicBottomTab: BottomDynamicTab | null = null;
  showCategoriesPreview = false;
  private categoryPreviewOrigin: 'home' | 'categories' = 'home';
  categoryPreviewItems: any[] = [];
  categoryPreviewProducts: any[] = [];
  categoryPreviewLoading = false;

  @Input() sections: CMSSection[] = [];
  @Input() tabs: Tab[] = [];
  @Input() activeTabId = '';
  @Input() themeName = 'default';
  @Input() themeId = 'default';
  @Input() availableCategories: CategoryDto[] = [];
  @Input() previewMode: 'home' | 'collection' = 'home';
  @Input() collectionTitle = 'Collection';
  @Input() collectionType: 'collection' | 'brand' | 'campaign' = 'collection';
  @Input() collectionIconUrl = '';
  @Input() collectionBannerUrl = '';
  @Input() collectionBackgroundColor = '#ffffff';
  @Input() collectionTextColor = '#111111';

  @Output() sectionSelected = new EventEmitter<string>();

  ngOnInit() {
    this.updateTime();
    this.timeTimerId = setInterval(() => this.updateTime(), 10000);
    this.themeService.getThemes().subscribe((themes) => {
      this.dynamicThemes = themes;
    });
    this.loadDynamicBottomTab();
  }

  ngOnDestroy() {
    if (this.timeTimerId) {
      clearInterval(this.timeTimerId);
    }
    this.shelfSubscriptions.forEach((subscription) =>
      subscription.unsubscribe()
    );
    this.categoryPreviewSubscription?.unsubscribe();
    this.dynamicTabSubscription?.unsubscribe();
  }

  private loadDynamicBottomTab() {
    this.dynamicTabSubscription = this.dynamicTabsService.getTabs().subscribe({
      next: (tabs) => {
        this.dynamicBottomTab =
          tabs
            .filter((tab) => tab.is_active)
            .sort(
              (a, b) =>
                (b.priority ?? 0) - (a.priority ?? 0) ||
                (b.id ?? 0) - (a.id ?? 0)
            )[0] || null;
      },
      error: () => {
        this.dynamicBottomTab = null;
      },
    });
  }

  ngDoCheck() {
    this.syncProductShelfPreviews();
  }

  private updateTime() {
    const now = new Date();
    this.currentTime =
      now.getHours() + ':' + now.getMinutes().toString().padStart(2, '0');
  }

  get config(): ThemeConfig {
    const theme =
      this.dynamicThemes.find((t) => t.id === this.themeId) ||
      this.dynamicThemes.find((t) => t.name === this.themeName);
    return theme?.theme_config || this.themeService.getDefaultConfig();
  }

  get headerStyle(): string {
    const c = this.config;
    if (c.headerBackgroundType === 'solid') return c.headerColorStart;
    return `linear-gradient(135deg, ${c.headerColorStart}, ${c.headerColorEnd})`;
  }

  get activeSearchPlaceholder(): string {
    const activeTab = this.tabs.find((t) => t.id === this.activeTabId);
    return (
      activeTab?.search_placeholder || 'Search for fruits, vegetables, milk...'
    );
  }

  getSafeSvg(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg || '');
  }

  onSectionClick(id: string) {
    this.sectionSelected.emit(id);
  }

  get categoryListPreviewGroups() {
    return this.availableCategories
      .filter((parent) => parent.isActive !== false)
      .map((parent) => ({
        name: parent.name,
        children: (parent.children || [])
          .filter((child) => child.isActive !== false)
          .map((child) => ({
            category_id: String(child.id),
            name: child.name,
            image_url: child.iconUrl || '',
          })),
      }))
      .filter((group) => group.children.length > 0);
  }

  openCategoriesPreview() {
    this.categoryPreviewSubscription?.unsubscribe();
    this.categoryPreviewSubscription = undefined;
    this.showCategoriesPreview = true;
    this.selectedPreviewCategory = null;
    this.categoryPreviewItems = [];
    this.categoryPreviewProducts = [];
    this.categoryPreviewLoading = false;
  }

  openHomePreview() {
    this.closeCategoryPreview();
    this.showCategoriesPreview = false;
  }

  openCategoryPreview(
    category: any,
    siblings: any[],
    origin: 'home' | 'categories' = 'home'
  ) {
    this.categoryPreviewOrigin = origin;
    this.categoryPreviewItems = siblings?.length ? siblings : [category];
    this.selectPreviewCategory(category);
  }

  selectPreviewCategory(category: any) {
    this.selectedPreviewCategory = category;
    this.categoryPreviewProducts = [];
    this.categoryPreviewLoading = true;
    this.categoryPreviewSubscription?.unsubscribe();

    const categoryId = Number(category?.category_id ?? category?.id);
    this.categoryPreviewSubscription = this.productsService
      .getProductsForShelfPreview({
        categoryIds: Number.isFinite(categoryId)
          ? this.getCategoryAndDescendantIds(categoryId)
          : [],
        limit: 20,
      })
      .subscribe({
        next: (products) => {
          this.categoryPreviewProducts = products.map((product) =>
            this.toShelfPreviewProduct(product)
          );
          this.categoryPreviewLoading = false;
        },
        error: () => {
          this.categoryPreviewProducts = [];
          this.categoryPreviewLoading = false;
        },
      });
  }

  closeCategoryPreview() {
    this.categoryPreviewSubscription?.unsubscribe();
    this.categoryPreviewSubscription = undefined;
    this.selectedPreviewCategory = null;
    this.categoryPreviewItems = [];
    this.categoryPreviewProducts = [];
    this.categoryPreviewLoading = false;
    this.showCategoriesPreview = this.categoryPreviewOrigin === 'categories';
  }

  isCustomerRenderedSection(type: string): boolean {
    const normalizedType = this.normalizeSectionType(type);
    if (this.previewMode === 'collection') {
      return [
        'banner',
        'collection_banner',
        'category_grid',
        'brand_grid',
        'featured_cards',
        'promo_carousel',
        'product_shelf',
      ].includes(normalizedType);
    }
    return !['offer_strip', 'flash_sale', 'hero_showcase'].includes(
      normalizedType
    );
  }

  normalizeSectionType(type: string): string {
    return String(type || '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '_');
  }

  sanitizeIcon(icon: string): string {
    if (!icon) return 'star';
    if (icon.includes('fi-rs-leaf')) return 'leaf';
    if (icon.includes('fi-rr-grocery-basket')) return 'shopping-basket';
    if (icon.includes('fi-rs-home')) return 'home';
    if (icon.includes('fi-rs-star')) return 'star';
    if (icon.includes('fi-rs-shopping-cart')) return 'shopping-cart';
    // If it's still a Flaticon class, extract the last part or fallback
    if (icon.includes('fi-')) {
      const parts = icon.split('-');
      return parts[parts.length - 1] || 'star';
    }
    return icon;
  }

  // Handle horizontal scroll with mouse wheel
  onWheel(event: WheelEvent) {
    if (event.deltaY !== 0) {
      const container = event.currentTarget as HTMLElement;
      container.scrollLeft += event.deltaY + event.deltaX;
      event.preventDefault();
    }
  }

  getSectionByType(type: string): CMSSection | undefined {
    return this.sections.find((s) => s.type === type);
  }

  getSectionConfig(section: CMSSection) {
    let data = section.config?.data ?? section.config ?? {};

    while (
      data &&
      typeof data === 'object' &&
      !Array.isArray(data) &&
      data.data &&
      typeof data.data === 'object' &&
      Object.keys(data).every((key) => key === 'data' || key === 'version')
    ) {
      data = data.data;
    }

    return data || {};
  }

  getHeroShowcaseConfig(section: CMSSection): HeroShowcaseConfig {
    return this.getSectionConfig(section);
  }

  getOfferStripConfig(section: CMSSection): OfferStripConfig {
    return this.getSectionConfig(section);
  }

  getBrandGridConfig(section: CMSSection): BrandGridConfig {
    return this.getSectionConfig(section);
  }

  getCollectionBannerConfig(section: CMSSection): CollectionBannerConfig {
    const config = this.getSectionConfig(section);
    const layoutAliases: Record<string, CollectionBannerConfig['layout_type']> =
      {
        overlay: 'overlay_cards',
        overlay_cards: 'overlay_cards',
        hero: 'hero_mosaic',
        hero_grid: 'hero_mosaic',
        mosaic: 'hero_mosaic',
        hero_mosaic: 'hero_mosaic',
        left: 'left_hero',
        left_hero: 'left_hero',
        bento: 'bento_grid',
        bento_grid: 'bento_grid',
        carousel: 'horizontal_carousel',
        horizontal: 'horizontal_carousel',
        horizontal_carousel: 'horizontal_carousel',
        glass: 'glassmorphism',
        glassmorphism: 'glassmorphism',
        spotlight: 'spotlight',
        product_picks: 'product_picks',
        festive_picks: 'product_picks',
        category_essentials: 'category_essentials',
        essentials: 'category_essentials',
      };
    const savedLayout = String(config.layout_type || config.layout || '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '_');

    return {
      ...config,
      title: config.title || '',
      background_type: config.background_type === 'gradient' ? 'gradient' : 'solid',
      background_color: config.background_color || '#7A2E0E',
      background_color_end: config.background_color_end || config.background_color || '#F6D7A4',
      gradient_angle: Number(config.gradient_angle ?? 180),
      layout_type: layoutAliases[savedLayout] || 'hero_mosaic',
      render_main_banner: config.render_main_banner !== false,
      edge_to_edge: config.edge_to_edge !== false,
      show_top_border: config.show_top_border === true,
      show_bottom_border: config.show_bottom_border === true,
      border_style: config.border_style || 'scallop',
      border_color: config.border_color || '#FFF6E4',
      border_under_color: config.border_under_color || '#FFFFFF',
      top_border_style: config.top_border_style || config.border_style || 'scallop',
      bottom_border_style: config.bottom_border_style || config.border_style || 'scallop',
      top_border_color: config.top_border_color || config.border_color || '#FFF6E4',
      bottom_border_color: config.bottom_border_color || config.border_color || '#FFF6E4',
      top_border_under_color: config.top_border_under_color || config.border_under_color || '#FFFFFF',
      bottom_border_under_color: config.bottom_border_under_color || config.border_under_color || '#FFFFFF',
      text_style: config.text_style || 'teej',
      product_style: config.product_style || 'product_card',
      chip_style: config.chip_style || 'circle',
      chip_selected_text_color: config.chip_selected_text_color || '#B45309',
      chip_unselected_text_color: config.chip_unselected_text_color || '#5A2A0A',
      chip_selected_border_color: config.chip_selected_border_color || '#B45309',
      title_color: config.title_color || config.text_color || '#5A2A0A',
      subtitle_color: config.subtitle_color || config.text_color || '#5A2A0A',
      show_bottom_offer: config.show_bottom_offer === true,
      data_source: config.data_source || { type: 'query', query: { limit: 8, sort_by: 'featured' }, products: [] },
      categories: Array.isArray(config.categories) ? config.categories : [],
      main_banner: config.main_banner || { image_url: '' },
      items: Array.isArray(config.items) ? config.items : [],
    };
  }

  hasCollectionCardTitle(item?: { title?: string | null }): boolean {
    return String(item?.title ?? '').trim().length > 0;
  }

  collectionOfferImages(col: CollectionBannerConfig): string[] {
    const fromSlots = (col.bottom_offer?.images || [])
      .map(image => String(image?.image_url || '').trim())
      .filter(Boolean);
    if (fromSlots.length) return fromSlots.slice(0, 3);
    const legacy = String(col.bottom_offer?.image_url || '').trim();
    return legacy ? [legacy] : [];
  }

  getCollectionSurface(col: CollectionBannerConfig): string {
    const start = col.background_color || '#7A2E0E';
    if (col.background_type === 'gradient') {
      const end = col.background_color_end || start;
      const angle = Number(col.gradient_angle ?? 180);
      return `linear-gradient(${angle}deg, ${start}, ${end})`;
    }
    return start;
  }

  getCollectionEdgeStyle(
    col: CollectionBannerConfig,
    side: 'top' | 'bottom',
  ): Record<string, string> {
    const color =
      (side === 'bottom' ? col.bottom_border_color : col.top_border_color) ||
      col.border_color ||
      '#FFF6E4';
    const under =
      (side === 'bottom' ? col.bottom_border_under_color : col.top_border_under_color) ||
      col.border_under_color ||
      '#FFFFFF';
    const kind =
      (side === 'bottom' ? col.bottom_border_style : col.top_border_style) ||
      col.border_style ||
      'scallop';
    const unit = kind === 'wave' ? 22 : kind === 'petal' ? 18 : 16;
    const height = kind === 'wave' ? 11 : kind === 'petal' ? 12 : 10;
    const radius = unit / 2;
    const extra = 2;
    const svgHeight = height + extra;
    const laceY = side === 'bottom' ? extra : 0;
    const overlapY = side === 'bottom' ? 0 : height;

    const toothPath =
      kind === 'wave'
        ? side === 'top'
          ? `M0 ${height} A${radius} ${radius} 0 0 1 ${unit} ${height} Z`
          : `M0 ${extra} A${radius} ${radius} 0 0 0 ${unit} ${extra} Z`
        : kind === 'petal'
          ? side === 'top'
            ? `M0 ${height} Q${radius} ${-height * 0.12} ${unit} ${height} Z`
            : `M0 ${extra} Q${radius} ${height * 1.12 + extra} ${unit} ${extra} Z`
          : side === 'top'
            ? `M0 ${height} L${radius} 0 L${unit} ${height} Z`
            : `M0 ${extra} L${radius} ${height + extra} L${unit} ${extra} Z`;

    const markup =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' +
      unit +
      '" height="' +
      svgHeight +
      '" viewBox="0 0 ' +
      unit +
      ' ' +
      svgHeight +
      '">' +
      '<rect y="' +
      laceY +
      '" width="' +
      unit +
      '" height="' +
      height +
      '" fill="' +
      under +
      '"/>' +
      '<rect y="' +
      overlapY +
      '" width="' +
      unit +
      '" height="' +
      extra +
      '" fill="' +
      color +
      '"/>' +
      '<path d="' +
      toothPath +
      '" fill="' +
      color +
      '"/>' +
      '</svg>';

    return {
      height: `${svgHeight}px`,
      backgroundColor: color,
      backgroundImage: 'url("data:image/svg+xml,' + encodeURIComponent(markup) + '")',
      backgroundRepeat: 'repeat-x',
      backgroundSize: `${unit}px ${svgHeight}px`,
      marginBottom: side === 'top' ? `-${extra}px` : '0px',
      marginTop: side === 'bottom' ? `-${extra}px` : '0px',
      position: 'relative',
      zIndex: '1',
    };
  }

  collectionHeroImage(col: CollectionBannerConfig): string {
    return col.background_image_url || col.main_banner?.image_url || '';
  }

  getVisualItems(section: CMSSection): any[] {
    const config = this.getSectionConfig(section);
    // Support 'banners' mapping for 'banner' type or generic 'items'
    if (section.type === 'banner' && config.banners) return config.banners;
    return config.items?.length ? config.items : [];
  }

  getPromoCards(section: CMSSection): any[] {
    return this.getSectionConfig(section).cards || [];
  }

  getShelfProducts(section: CMSSection): any[] {
    const config = this.getSectionConfig(section);
    if (config.data_source?.type === 'manual') {
      return config.data_source?.products || [];
    }
    return this.shelfPreviewProducts.get(section.id) || [];
  }

  getProductShelfUiType(config: any): 'grid' | 'two_row' {
    return config?.ui_type === 'two_row' ? 'two_row' : 'grid';
  }

  shouldShowShelfSeeAll(section: CMSSection): boolean {
    const config = this.getSectionConfig(section);
    return (
      config.show_see_all !== false &&
      Boolean(config.data_source?.query?.category_id) &&
      Number(config.data_source?.query?.limit || 0) > 6 &&
      this.getShelfProducts(section).length > 6
    );
  }

  productImage(product: any): string {
    return String(product?.image || product?.image_url || '');
  }

  productDiscount(product: any): number {
    const price = Number(product?.price || 0);
    const mrp = Number(product?.mrp || 0);
    return mrp > price && mrp > 0
      ? Math.round(((mrp - price) / mrp) * 100)
      : 0;
  }

  productFeaturedHighlight(product: any): string {
    if (product?.featuredHighlight) return String(product.featuredHighlight);
    if (!Array.isArray(product?.highlights)) return '';
    return String(
      product.highlights.find((highlight: any) => highlight?.is_featured === true)
        ?.value || ''
    ).trim();
  }

  isShelfLoading(sectionId: string): boolean {
    return this.loadingShelfIds.has(sectionId);
  }

  didShelfFail(sectionId: string): boolean {
    return this.failedShelfIds.has(sectionId);
  }

  private syncProductShelfPreviews() {
    this.sections
      .filter((section) => {
        if (section.type === 'product_shelf') return true;
        if (section.type !== 'collection_banner') return false;
        const layout = this.getSectionConfig(section).layout_type;
        return layout === 'product_picks' || layout === 'category_essentials';
      })
      .forEach((section) => {
        const config = this.getSectionConfig(section);
        if (config.data_source?.type === 'manual') return;

        const query = { ...(config.data_source?.query || {}) };
        if (
          section.type === 'collection_banner' &&
          config.layout_type === 'category_essentials' &&
          !query.category_id &&
          config.categories?.[0]?.category_id
        ) {
          query.category_id = config.categories[0].category_id;
        }
        if (!query.category_id) return;
        const categoryIds = query.category_id
          ? this.getCategoryAndDescendantIds(Number(query.category_id))
          : [];
        const signature = JSON.stringify({
          categoryIds,
          brandId: query.brand_id || null,
          sortBy: query.sort_by || 'featured',
          limit: query.limit || 10,
        });
        if (this.shelfQuerySignatures.get(section.id) === signature) return;

        this.shelfQuerySignatures.set(section.id, signature);
        this.shelfSubscriptions.get(section.id)?.unsubscribe();
        this.loadingShelfIds.add(section.id);
        this.failedShelfIds.delete(section.id);
        this.shelfPreviewProducts.set(section.id, []);

        const subscription = this.productsService
          .getProductsForShelfPreview({
            categoryIds,
            brandId: query.brand_id ? Number(query.brand_id) : undefined,
            sortBy: query.sort_by,
            limit: query.limit,
          })
          .subscribe({
            next: (products) => {
              this.shelfPreviewProducts.set(
                section.id,
                products.map((product) => this.toShelfPreviewProduct(product))
              );
              this.loadingShelfIds.delete(section.id);
            },
            error: () => {
              this.loadingShelfIds.delete(section.id);
              this.failedShelfIds.add(section.id);
            },
          });
        this.shelfSubscriptions.set(section.id, subscription);
      });
  }

  private getCategoryAndDescendantIds(categoryId: number): number[] {
    const find = (categories: CategoryDto[]): CategoryDto | undefined => {
      for (const category of categories) {
        if (category.id === categoryId) return category;
        const child = find(category.children || []);
        if (child) return child;
      }
      return undefined;
    };
    const collect = (category: CategoryDto): number[] => [
      category.id,
      ...(category.children || []).flatMap(collect),
    ];
    const category = find(this.availableCategories);
    return category ? collect(category) : [categoryId];
  }

  private toShelfPreviewProduct(product: Product) {
    const variant =
      product.variants?.find((item) => item.is_active !== false) ||
      product.variants?.[0];
    return {
      id: product.id,
      name: product.name,
      brand: product.brand?.name || '',
      unit: variant
        ? getVariantDisplayQuantity(variant)
        : '',
      price: variant?.price ?? variant?.mrp ?? 0,
      mrp: variant?.mrp ?? variant?.price ?? 0,
      image_url:
        product.images?.find((image) => image.isPrimary)?.url ||
        product.images?.[0]?.url ||
        '',
      variantCount: (product.variants || []).length,
      food_type: product.food_type,
      highlights: product.highlights || [],
      featuredHighlight:
        product.highlights
          ?.find((highlight) => highlight?.is_featured === true)
          ?.value?.trim() || '',
      rating: Number((product as any).rating || 0),
    };
  }

  getFlashSaleItems(_section: CMSSection): any[] {
    return [
      {
        name: 'Cold Pressed Coconut Oil',
        price: 199,
        mrp: 350,
        discount: '43% OFF',
        image: '',
      },
      {
        name: 'Organic Honey (500g)',
        price: 149,
        mrp: 299,
        discount: '50% OFF',
        image: '',
      },
      {
        name: 'Premium Cashews (250g)',
        price: 299,
        mrp: 450,
        discount: '33% OFF',
        image: '',
      },
    ];
  }

  getBrandItems(section: CMSSection): any[] {
    const config = this.getBrandGridConfig(section);
    const brands = config.brands || [];
    if (brands.length) return brands;

    return [
      {
        name: "Haldiram's",
        image:
          'https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/Haldiram_Logo.svg/1200px-Haldiram_Logo.svg.png',
      },
      {
        name: 'Durex',
        image:
          'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3f/Durex_logo.svg/2560px-Durex_logo.svg.png',
      },
      {
        name: 'Cadbury',
        image:
          'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Cadbury_Logo.svg/2560px-Cadbury_Logo.svg.png',
      },
      {
        name: 'Maggi',
        image:
          'https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/Maggi_logo.svg/2560px-Maggi_logo.svg.png',
      },
      {
        name: 'Nescafe',
        image:
          'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Nescafe_logo.svg/2560px-Nescafe_logo.svg.png',
      },
    ];
  }

  getGridCategories(section: CMSSection): any[] {
    const config = this.getSectionConfig(section);
    const cats = config.categories || [];
    if (cats.length) {
      return cats
        .map((c: any) => {
          // Handle both simple ID arrays and CategoryLink object arrays
          const id =
            c.category_id ||
            (typeof c === 'string' || typeof c === 'number' ? c : null);
          if (!id) return null;
          const found = this.findCategoryById(id);
          if (found) {
            return {
              name: c.name || found.name,
              image:
                c.image_url ||
                found.iconUrl ||
                'https://cdn-icons-png.flaticon.com/512/1625/1625048.png',
            };
          }
          return c; // Fallback to provided data
        })
        .filter(Boolean);
    }

    return [
      {
        name: 'Fruits',
        image: 'https://cdn-icons-png.flaticon.com/512/1625/1625048.png',
      },
      {
        name: 'Vegetables',
        image: 'https://cdn-icons-png.flaticon.com/512/2329/2329903.png',
      },
      {
        name: 'Dairy & Bread',
        image: 'https://cdn-icons-png.flaticon.com/512/2674/2674486.png',
      },
      {
        name: 'Munchies',
        image: 'https://cdn-icons-png.flaticon.com/512/2553/2553691.png',
      },
      {
        name: 'Cold Drinks',
        image: 'https://cdn-icons-png.flaticon.com/512/2405/2405479.png',
      },
      {
        name: 'Personal Care',
        image: 'https://cdn-icons-png.flaticon.com/512/3138/3138851.png',
      },
      {
        name: 'Chicken & Meat',
        image: 'https://cdn-icons-png.flaticon.com/512/1046/1046769.png',
      },
      {
        name: 'Home Care',
        image: 'https://cdn-icons-png.flaticon.com/512/2569/2569106.png',
      },
    ];
  }

  getCategoryGroups(section: CMSSection): any[] {
    const config = this.getSectionConfig(section);
    const existingGroups = Array.isArray(config.category_groups)
      ? config.category_groups
          .map((group: any) => ({
            ...group,
            children: (group.children || []).filter(
              (child: any) =>
                child.isActive !== false && child.is_active !== false
            ),
          }))
          .filter((group: any) => group.children.length > 0)
      : [];

    if (existingGroups.length) return existingGroups;

    const selected = Array.isArray(config.categories)
      ? config.categories.filter(
          (category: any) =>
            category.isActive !== false && category.is_active !== false
        )
      : [];

    return selected.map((category: any) => {
      const id = category.category_id ?? category.id;
      const parent = this.findCategoryById(id);
      const children = (parent?.children || [])
        .filter((child) => child.isActive !== false)
        .map((child) => ({
          name: child.name,
          image_url: child.iconUrl,
          category_id: String(child.id),
          isActive: child.isActive,
        }));

      return {
        parentName: category.name || parent?.name || 'Categories',
        parentId: String(id),
        children: children.length
          ? children
          : [
              {
                name: category.name || parent?.name || 'Category',
                image_url: category.image_url || parent?.iconUrl || '',
                category_id: String(id),
                isActive: true,
              },
            ],
      };
    });
  }

  findCategoryById(id: number | string): CategoryDto | undefined {
    const numericId = Number(id);
    const search = (cats: CategoryDto[]): CategoryDto | undefined => {
      for (const cat of cats) {
        if (cat.id === numericId) return cat;
        if (cat.children?.length) {
          const found = search(cat.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    return search(this.availableCategories);
  }

  splitIntoChunks(array: any[], size: number): any[][] {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }
}
